// src/riskFlow/RiskFlowWrapperV2.js
// Drives the single-risk micro-wizard and dispatches progress to Redux.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Center, Stack, Text } from '@mantine/core';
// RTK slice actions
import {
  registerRiskSources,
  toggleRiskSource,
} from '../redux/slices/riskSourcesSlice';
import {
  setAreaNotApplicable,
  clearAreaStatus,
  setAreaStatus,
} from '../redux/slices/assessmentProgressSlice';
import {
  updateAssessmentPositionLocal,
} from '../redux/slices/assessmentsSlice';

import RiskAreaIntro from './RiskAreaIntro';
import RiskSources from './RiskSources';
import RiskCauseAnalysis from './RiskCauseAnalysis';
import RiskAnalysis from './RiskAnalysis';
import FastTrackRiskEvaluation from './FastTrackRiskEvaluation';
import RiskActionPlan from './RiskActionPlan';
import ControlEvaluationWizard from './ControlEvaluationWizard';
import ResidualRiskAssessment from './ResidualRiskAssessment';
import RiskSummaryReport from './RiskSummaryReport';
import TwoPaneLayout from './TwoPaneLayout';
import LeftPaneQueue from './LeftPaneQueue';
import { Navigation } from '../components/NavigationButtons';
import { selectAreaStatus } from '../redux/slices/assessmentProgressSlice';

const STEPS = {
  INTRO: 'intro',
  SOURCES: 'sources',
  CAUSE: 'cause',
  ANALYSIS: 'analysis',
  EVAL: 'eval',
  ACTION_PLAN: 'actionPlan',
  CONTROL_EVAL: 'controlEval',
  RESIDUAL: 'residual',
  SUMMARY: 'summary',
};

// New phases for right pane sliding UX
const PHASES = {
  SOURCE_SELECTION: 'source_selection',
  SOURCE_PROCESSING: 'source_processing', 
  RISK_EVALUATION: 'risk_evaluation',
  COMPLETE: 'complete'
};

export default function RiskFlowWrapperV2(props) {
  const {
    // modelId removed - RTK actions use assessmentUuid instead (modelId still passed via {...props} to children)
    riskArea,
    narratives: modelNarratives = {},
    onAreaDone,
    onSourceChange,
    assessmentUuid,
    savedPosition, // Add savedPosition prop for restoration
  } = props;
  
  const dispatch = useDispatch();

  /* new phase-based state management with restoration */
  const getInitialPhase = useCallback(() => {
    // Only restore phase if it's for the current area
    if (!savedPosition?.phase || savedPosition.section !== riskArea) {
      return PHASES.SOURCE_SELECTION;
    }
    if (savedPosition.phase === 'source_processing') return PHASES.SOURCE_PROCESSING;
    if (savedPosition.phase === 'risk_evaluation') return PHASES.RISK_EVALUATION;
    if (savedPosition.phase === 'complete') return PHASES.COMPLETE;
    return PHASES.SOURCE_SELECTION;
  }, [savedPosition?.phase, savedPosition?.section, riskArea]);

  // Debug: Log component mounting for area transitions
  useEffect(() => {
    console.log(`[RiskFlowWrapper] Component mounted for area: ${riskArea}`, {
      initialPhase: getInitialPhase(),
      savedPosition: savedPosition?.section,
      timestamp: new Date().toISOString()
    });
  }, [riskArea, getInitialPhase, savedPosition?.section]); // Include all dependencies

  /* bootstrap canonical sources once */
  const riskSources = useSelector(
    (s) => s.riskSources.byAssessment[assessmentUuid]?.[riskArea]?.riskSources || []
  );
  const areaStatus = useSelector((s) => selectAreaStatus(s, assessmentUuid, riskArea));
  
  useEffect(() => {
    if (!riskSources.length) {
      const DEFAULT = ['Data Set', 'Model', 'Supply Chain', 'Environment'];
      // Filter out "Other" from default sources
      const filteredDefault = DEFAULT.filter(src => src !== 'Other');
      dispatch(registerRiskSources({ assessmentUuid, riskArea, names: filteredDefault }));
    }
  }, [riskSources.length, dispatch, assessmentUuid, riskArea]);

  /* Helper to restore state from saved position - memoized to prevent infinite loops */
  const getInitialStateFromPosition = useCallback(() => {
    // Only restore if this is the correct area and we have position data
    if (!savedPosition || savedPosition.section !== riskArea || savedPosition.majorStep !== 'safetyRisks') {
      return {
        step: STEPS.INTRO,
        selected: [],
        idx: 0
      };
    }

    // Restore step from subStep
    const restoredStep = savedPosition.subStep || STEPS.INTRO;
    
    // Restore selected sources - we'll need to get them from Redux
    // For now, start with empty and let useEffect handle it
    const restoredSelected = [];
    
    // Restore source index
    const restoredIdx = savedPosition.sourceIndex || 0;

    return {
      step: restoredStep,
      selected: restoredSelected,
      idx: restoredIdx
    };
  }, [savedPosition, riskArea]);

  const initialState = getInitialStateFromPosition();

  /* local FSM - legacy step-based (being replaced with phase-based) */
  const [step, setStep] = useState(initialState.step);
  const [selected, setSelected] = useState(initialState.selected); // names chosen in sources screen
  const [idx, setIdx] = useState(initialState.idx); // pointer → current source
  
  const [currentPhase, setCurrentPhase] = useState(getInitialPhase);
  const [sourceQueue, setSourceQueue] = useState([]);
  const [riskQueue, setRiskQueue] = useState([]);
  const [currentProcessingItem, setCurrentProcessingItem] = useState(null);
  const [processingStep, setProcessingStep] = useState(
    savedPosition?.subStep || STEPS.CAUSE
  );
  const [selectedRisk, setSelectedRisk] = useState(null); // For reporting phase risk selection
  const restoredPositionRef = useRef(null);
  
  /* Reset state when riskArea changes (moving to new area) or restore from position */
  useEffect(() => {
    const newState = getInitialStateFromPosition();
    setStep(newState.step);
    setSelected(newState.selected);
    setIdx(newState.idx);
  }, [getInitialStateFromPosition]);
  
  /* Get Redux data for restoration */
  // Note: selectedSources removed as it's not used in the simplified phase-based system
  const allAreaRisks = useSelector(s =>
    s.riskItems.byAssessment[assessmentUuid]?.[riskArea]?.areaRisks || {}
  );
  
  /* Function to restore reporting phase from Redux data */
  const restoreReportingPhase = useCallback(() => {
    // Rebuild risk queue from Redux areaRisks
    const allSavedRisks = [];
    Object.keys(allAreaRisks || {}).forEach(sourceName => {
      const sourceRisks = allAreaRisks[sourceName] || [];
      sourceRisks.forEach(risk => {
        if (risk.status === 'Saved') {
          allSavedRisks.push({
            ...risk,
            sourceName,
            status: 'completed' // Mark as completed for reporting phase
          });
        }
      });
    });

    if (allSavedRisks.length > 0) {
      setRiskQueue(allSavedRisks);
      setSelectedRisk(allSavedRisks[0]); // Select first risk
      setCurrentProcessingItem({
        type: 'reporting',
        areaName: riskArea,
        completedSources: 0, // We don't have sourceQueue in restore
        completedRisks: allSavedRisks.length
      });
      setProcessingStep('reporting');
    }
  }, [allAreaRisks, riskArea]);
  
  /* Simple one-time restoration effect */
  useEffect(() => {
    if (!savedPosition?.phase || !riskSources.length) return;
    
    // Only restore if position is for current area
    if (savedPosition.section !== riskArea) return;
    
    // Create a stable key for this position
    const positionKey = `${savedPosition.phase}-${savedPosition.sourceName}-${savedPosition.riskName}-${riskArea}`;
    
    // Only restore once per position
    if (restoredPositionRef.current === positionKey) return;
    restoredPositionRef.current = positionKey;
    
    // Only do restoration for phase-based positions
    if (savedPosition.phase === 'source_processing' && savedPosition.sourceName) {
      setCurrentProcessingItem({ name: savedPosition.sourceName });
    } else if (savedPosition.phase === 'risk_evaluation' && savedPosition.riskName) {
      // Need to find the complete risk object from Redux to get riskId
      const sourceName = savedPosition.sourceName || 'Unknown Source';
      const sourceRisks = allAreaRisks[sourceName] || [];
      const matchedRisk = sourceRisks.find(risk =>
        risk.title === savedPosition.riskName || risk.riskName === savedPosition.riskName
      );
      
      if (matchedRisk) {
        // Use the complete risk object with riskId
        const restoredRisk = {
          ...matchedRisk,
          sourceName: sourceName
        };
        console.log('[RiskFlowWrapper] Restoring risk with complete object:', {
          riskId: restoredRisk.riskId,
          riskName: restoredRisk.title || restoredRisk.riskName,
          sourceName: restoredRisk.sourceName
        });
        setCurrentProcessingItem(restoredRisk);
      } else {
        // Fallback to simple object (should rarely happen)
        console.log('[RiskFlowWrapper] Could not find complete risk object, using fallback');
        setCurrentProcessingItem({
          riskName: savedPosition.riskName,
          sourceName: sourceName
        });
      }
    } else if (savedPosition.phase === 'complete') {
      // Restore reporting phase - need to rebuild risk queue from Redux
      restoreReportingPhase();
    }
  }, [savedPosition?.phase, savedPosition?.sourceName, savedPosition?.riskName, savedPosition?.section, riskArea, riskSources.length, restoreReportingPhase, allAreaRisks]);
  
  const curName = selected[idx];
  const isSkipped = areaStatus === 'NA';

  // Legacy mark function removed - now handled by phase transitions

  /* new phase-based helpers */
  const initializeSourceQueue = (selectedSources) => {
    const queue = selectedSources.map(sourceName => {
      const sourceObj = riskSources.find(s => s.name === sourceName) || { name: sourceName };
      return {
        ...sourceObj,
        status: 'waiting', // waiting, processing, completed
        savedRisk: null
      };
    });
    setSourceQueue(queue);
    if (queue.length > 0) {
      setCurrentProcessingItem(queue[0]);
      setCurrentPhase(PHASES.SOURCE_PROCESSING);
      setProcessingStep(STEPS.CAUSE);
    }
  };

  const completeSourceProcessing = (sourceName, savedRisk) => {
    // Update source queue to mark current source as completed
    setSourceQueue(prev => 
      prev.map(source => 
        source.name === sourceName 
          ? { ...source, status: 'completed', savedRisk }
          : source
      )
    );
    
    // Move to next source or transition to risk processing
    const currentIndex = sourceQueue.findIndex(s => s.name === sourceName);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < sourceQueue.length) {
      // More sources to process
      const nextSource = sourceQueue[nextIndex];
      setCurrentProcessingItem(nextSource);
      setProcessingStep(STEPS.CAUSE);
    } else {
      // All sources done, move to risk processing phase
      // Get all saved risks from Redux allAreaRisks instead of sourceQueue
      const allSavedRisks = [];
      Object.keys(allAreaRisks || {}).forEach(sourceName => {
        const sourceRisks = allAreaRisks[sourceName] || [];
        sourceRisks.forEach(risk => {
          if (risk.status === 'Saved') {
            allSavedRisks.push({
              ...risk,
              sourceName, // Add source name for reference
              status: 'waiting' // Set queue status to waiting
            });
          }
        });
      });
      
      setRiskQueue(allSavedRisks);
      
      if (allSavedRisks.length > 0) {
        setCurrentProcessingItem(allSavedRisks[0]);
        setCurrentPhase(PHASES.RISK_EVALUATION);
        setProcessingStep(STEPS.EVAL);
      } else {
        setCurrentPhase(PHASES.COMPLETE);
        onAreaDone?.();
      }
    }
  };

  const completeRiskProcessing = (riskId) => {
    // Find current and next index using current state
    const currentIndex = riskQueue.findIndex(r => r.riskId === riskId);
    const nextIndex = currentIndex + 1;
    
    // Update risk queue to mark current risk as completed
    const updatedQueue = riskQueue.map(risk => 
      risk.riskId === riskId 
        ? { ...risk, status: 'completed' }
        : risk
    );
    setRiskQueue(updatedQueue);
    
    if (nextIndex < riskQueue.length) {
      // More risks to process - move directly to next risk
      const nextRisk = updatedQueue[nextIndex];
      setCurrentProcessingItem(nextRisk);
      setProcessingStep(STEPS.EVAL);
    } else {
      // All risks processed - transition to reporting phase
      // Mark all remaining risks as completed
      const allCompletedRisks = updatedQueue.map(risk => ({ ...risk, status: 'completed' }));
      setRiskQueue(allCompletedRisks);
      
      setCurrentPhase(PHASES.COMPLETE);
      setCurrentProcessingItem({ 
        type: 'reporting',
        areaName: riskArea,
        completedSources: sourceQueue.length,
        completedRisks: allCompletedRisks.length
      });
      setProcessingStep('reporting');
      // Initialize with first risk selected for reporting
      setSelectedRisk(allCompletedRisks[0] || null);
    }
  };


  /* handle position tracking for phase-based system */
  useEffect(() => {
    if (!assessmentUuid) return;

    if (currentPhase === PHASES.SOURCE_PROCESSING && currentProcessingItem?.name) {
      const position = {
        majorStep: 'safetyRisks',
        section: riskArea,
        subStep: processingStep,
        sourceName: currentProcessingItem.name,
        phase: 'source_processing'
      };
      dispatch(updateAssessmentPositionLocal({ assessmentUuid, position }));
    } else if (currentPhase === PHASES.RISK_EVALUATION && currentProcessingItem?.riskName) {
      const position = {
        majorStep: 'safetyRisks',
        section: riskArea,
        subStep: processingStep,
        riskName: currentProcessingItem.riskName,
        riskId: currentProcessingItem.riskId,
        sourceName: currentProcessingItem.sourceName,
        phase: 'risk_evaluation'
      };
      dispatch(updateAssessmentPositionLocal({ assessmentUuid, position }));
    } else if (currentPhase === PHASES.COMPLETE) {
      const position = {
        majorStep: 'safetyRisks',
        section: riskArea,
        subStep: 'reporting',
        phase: 'complete'
      };
      dispatch(updateAssessmentPositionLocal({ assessmentUuid, position }));
    }
  }, [currentPhase, processingStep, currentProcessingItem?.name, currentProcessingItem?.riskName, currentProcessingItem?.riskId, currentProcessingItem?.sourceName, dispatch, riskArea, assessmentUuid]);

  /* get current area risks for auto-save logic */
  const areaRisks = useSelector(s =>
    s.riskItems.byAssessment[assessmentUuid]?.[riskArea]?.areaRisks?.[curName] || []
  );

  /* get all section risks for the current risk area - using the one declared earlier for restoration */

  /* skip / re-apply handler - updated to use RTK slice actions */
  const handleSkipToggle = () => {
    if (isSkipped) {
      // Re-enable the area
      dispatch(clearAreaStatus({ assessmentUuid, areaName: riskArea }));
    } else {
      // Mark as not applicable (legacy behavior)
      dispatch(setAreaNotApplicable({ assessmentUuid, areaName: riskArea }));
      onAreaDone?.();
    }
  };

  /* render FSM - now phase-based */
  switch (currentPhase) {
    case PHASES.SOURCE_SELECTION:
      // Show intro or sources selection
      if (step === STEPS.INTRO) {
        return (
          <RiskAreaIntro
            {...props}
            assessmentUuid={assessmentUuid}
            onNext={() => setStep(STEPS.SOURCES)}
            onSkipToggle={handleSkipToggle}
          />
        );
      }
      
      if (step === STEPS.SOURCES) {
        return (
          <RiskSources
            {...props}
            onBack={() => setStep(STEPS.INTRO)}
            onNext={(arrObjs) => {
              /**
               * Sync Redux .selected flags to match arrObjs.
               * Our action toggles, so we diff current vs desired.
               */
              const desired = new Set(arrObjs.map(o => o.name));
              const currentlySelected = new Set(
                (riskSources || []).filter(s => s.selected).map(s => s.name)
              );

              // Turn OFF any currently selected that are not desired
              currentlySelected.forEach(n => {
                if (!desired.has(n)) {
                  dispatch(toggleRiskSource({ assessmentUuid, riskArea, srcName: n }));
                }
              });
              // Turn ON any desired not currently selected
              desired.forEach(n => {
                if (!currentlySelected.has(n)) {
                  dispatch(toggleRiskSource({ assessmentUuid, riskArea, srcName: n }));
                }
              });

              const selectedNames = Array.from(desired);
              setSelected(selectedNames);

              // Initialize the new queue-based flow
              initializeSourceQueue(selectedNames);
              onSourceChange?.(selectedNames[0] || null, 0);
            }}
          />
        );
      }
      return null;

    case PHASES.SOURCE_PROCESSING:
      // Single-pane layout: current source processing (progress shown in top-level indicator)
      return (
        <Container fluid px="xl" py="md">
          {currentProcessingItem && (
            <>
              {processingStep === STEPS.CAUSE && (
                <RiskCauseAnalysis
                  {...props}
                  source={currentProcessingItem}
                  onNext={() => setProcessingStep(STEPS.ANALYSIS)}
                  onBack={() => {
                    setCurrentPhase(PHASES.SOURCE_SELECTION);
                    setStep(STEPS.SOURCES);
                  }}
                />
              )}
              {processingStep === STEPS.ANALYSIS && (
                <RiskAnalysis
                  {...props}
                  modelNarratives={modelNarratives}
                  source={currentProcessingItem}
                  nextStep={(() => {
                    const currentIndex = sourceQueue.findIndex(s => s.name === currentProcessingItem?.name);
                    const nextIndex = currentIndex + 1;
                    if (nextIndex < sourceQueue.length) {
                      return `Continue to ${sourceQueue[nextIndex].name}`;
                    } else {
                      return 'Continue to Risk Evaluation';
                    }
                  })()}
                  onNext={() => {
                    // Get the saved risk from Redux and complete source processing
                    const savedRisk = areaRisks.find(risk => risk.status === 'Saved');
                    completeSourceProcessing(currentProcessingItem?.name, savedRisk);
                  }}
                  onBack={() => setProcessingStep(STEPS.CAUSE)}
                />
              )}
            </>
          )}
        </Container>
      );

    case PHASES.RISK_EVALUATION:
      // Single-pane layout: current risk processing (progress shown in top-level indicator)
      return (
        <Container fluid px="xl" py="md">
          {currentProcessingItem && (
            <>
              {processingStep === STEPS.EVAL && (
                <FastTrackRiskEvaluation
                  {...props}
                  source={{ name: currentProcessingItem?.sourceName }}
                  onNext={() => setProcessingStep(STEPS.ACTION_PLAN)}
                  onBack={() => setCurrentPhase(PHASES.SOURCE_PROCESSING)}
                />
              )}
              {processingStep === STEPS.ACTION_PLAN && (
                <RiskActionPlan
                  {...props}
                  source={riskSources.find(s => s.name === currentProcessingItem?.sourceName)}
                  onNext={() => setProcessingStep(STEPS.CONTROL_EVAL)}
                  onBack={() => setProcessingStep(STEPS.EVAL)}
                />
              )}
              {processingStep === STEPS.CONTROL_EVAL && (
                <ControlEvaluationWizard
                  {...props}
                  source={{ name: currentProcessingItem?.sourceName }}
                  onNext={() => setProcessingStep(STEPS.RESIDUAL)}
                  onBack={() => setProcessingStep(STEPS.ACTION_PLAN)}
                />
              )}
              {processingStep === STEPS.RESIDUAL && (
                <ResidualRiskAssessment
                  {...props}
                  source={{ name: currentProcessingItem?.sourceName }}
                  onNext={() => completeRiskProcessing(currentProcessingItem?.riskId)}
                  onBack={() => setProcessingStep(STEPS.CONTROL_EVAL)}
                  nextLabel={
                    (() => {
                      const currentIndex = riskQueue.findIndex(r => r.riskId === currentProcessingItem?.riskId);
                      const isLastRisk = currentIndex === riskQueue.length - 1;
                      return isLastRisk ? "Continue to Area Risk Summary" : "Continue to Next Risk";
                    })()
                  }
                />
              )}
            </>
          )}
        </Container>
      );

    case PHASES.COMPLETE:
      // Monitoring & Reporting phase
      // Show loading state if no risks loaded yet
      if (riskQueue.length === 0) {
        return (
          <Container fluid px="xl" py="xl">
            <Center style={{ minHeight: '400px' }}>
              <Stack align="center" spacing="md">
                <Text size="lg" color="dimmed">Loading assessment summary...</Text>
              </Stack>
            </Center>
          </Container>
        );
      }

      return (
        <TwoPaneLayout 
          phase={currentPhase}
          showRightPane={!!selectedRisk}
          leftContent={
            <LeftPaneQueue
              items={riskQueue}
              currentItem={selectedRisk}
              type="risks"
              phase={currentPhase}
              onItemClick={(risk) => {
                setSelectedRisk(risk);
              }}
            />
          }
          rightContent={
            <Stack spacing="md">
              {/* Individual Risk Summary Content */}
              {selectedRisk && (
                <RiskSummaryReport
                  {...props}
                  riskArea={riskArea}
                  source={{ name: selectedRisk.sourceName }}
                  selectedRisk={selectedRisk}
                  onBack={() => {
                    // Navigate back to the individual risk's residual assessment
                    setCurrentProcessingItem(selectedRisk);
                    setCurrentPhase(PHASES.RISK_EVALUATION);
                    setProcessingStep(STEPS.RESIDUAL);
                  }}
                  isConsolidatedView={false}
                />
              )}

              {/* Always visible Navigation for area completion */}
              <Navigation
                onNext={() => {
                  console.log('[RiskFlowWrapper] Area completion triggered from permanent navigation');
                  dispatch(setAreaStatus({ assessmentUuid, areaName: riskArea, status: 'COMPLETE' }));
                  onAreaDone?.();
                }}
                nextLabel="Move to Next Area"
                showBack={false}
              />
            </Stack>
          }
        />
      );

    default:
      return null;
  }
}